// --- Pattern Validation Definitions ---
import i18n from "@/language/i18n";

// Define a type for a pattern object
export type PatternObject = {
    regex: string;
    message: string;
};

// Define a union type for the pattern property in FormField
export type FieldPattern = string | PatternObject;

// Centralized mapping of pattern names to their regex and default messages
const validationPatterns: Record<string, PatternObject> = {
    PasswordValidateExp: {
        regex: "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[^\\da-zA-Z]).{8,}$",
        message: i18n.t("validation.password"),
    },
    AlphaNumeric: {
        regex: "^[A-Za-z0-9]+$",
        message: i18n.t("validation.alphaNumeric"),
    },
    AlphaNumericWithSpace: {
        regex: "^[A-Za-z0-9 ]+$",
        message: i18n.t("validation.alphaNumericWithSpace"),
    },
    AlphabetsWithSpaceCommaHyphenDotAndSmallBrackets: {
        regex: "^[A-Za-z\\s,\\-().]+$",
        message: i18n.t("validation.alphabetsWithSpaceCommaHyphenDotAndSmallBrackets"),
    },
    AlphaNumericWithSpaceCommaHyphenDotAndSmallBrackets: {
         regex: "^[A-Za-z0-9 ,().-]+$",
        message: i18n.t("validation.alphaNumericWithSpaceCommaHyphenDotAndSmallBrackets"),
    },
    Alphabets: {
        regex: "^[A-Za-z]+$",
        message: i18n.t("validation.alphabets"),
    },
    NationalID: {
        regex: "^[A-Za-z0-9-/]+$",
        message: i18n.t("validation.nationalId"),
    },
    AlphabetsEngAr: {
        regex: "^[A-Za-z\\u0600-\\u06FF\\u0750-\\u077F\\u08A0-\\u08FF\\uFB50-\\uFDFF\\uFE70-\\uFEFF]+$",
        message: i18n.t("validation.alphabetsEngAr"),
    },
    AlphabetsWithSpace: {
        regex: "^[A-Za-z ]+$",
        message: i18n.t("validation.alphabetsWithSpace"),
    },
    AlphabetsWithSpaceEngAr: {
        regex: "^[A-Za-z\\u0600-\\u06FF ]+$",
        message: i18n.t("validation.alphabetsWithSpaceEngAr"),
    },
    AlphabetsWithSpaceCommaHypeDot: {
        regex: "^[A-Za-z\\u0600-\\u06FF0-9.,\\- ]+$",
        message: i18n.t("validation.alphabetsWithSpaceCommaHypeDot"),
    },
    AlphaNumericWithSpaceHyphenApostropheAmpersandCommaDotSlashBrackets: {
        //   regex: "^(?!.* {2,})[A-Za-z\\u0600-\\u06FF0-9\\s&/\\\\,\\.\\?\\-\\[\\]\\(\\)_]+$",
        regex: "^(?!.* {2,})[A-Za-z\\u0600-\\u06FF0-9\\-.'&(),/ ]+$",
        message: i18n.t("validation.alphaNumericWithSpaceHyphenApostropheAmpersandCommaDotSlashBrackets"),
    },
    AlphabetsWithSpaceAr: {
        regex: "^[\\u0621-\\u064A\\u064B-\\u0652\\s]+$",
        message: i18n.t("validation.alphabetsWithSpaceAr"),
    },
    ArabicOnly: {
        regex: "^[\\u0600-\\u06FF\\s]+$",
        message: i18n.t("validation.arabicOnly"),
    },
    AlphabetsWithSpaceCommaHypeDotAr: {
        regex: "^[\\u0600-\\u06FF0-9.,\\- ]+$",
        message: i18n.t("validation.alphabetsWithSpaceCommaHypeDotAr"),
    },
    AlphanumericWithSpaceAndSymbols: {
        regex: "^[A-Za-z\\u0600-\\u06FF0-9\\s\\-&]+$", // Allows English + Arabic + digits + space + - &
        message: i18n.t("validation.alphanumericWithSpaceAndSymbols"),
    },
    PhoneNumber: {
        regex: "^(?!.*\\s)\\+?[0-9]{7,20}$",
        message: i18n.t("validation.phoneNumber"),
    },
    MobileNumber: {
        regex: "^\\+?\\d{7,20}$",
        message: i18n.t("validation.mobileNumber"),
    },
    Email: {
        regex: "^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
        message: i18n.t("validation.email"),
    },
    Website: {
        // eslint-disable-next-line no-useless-escape
        regex: "^https?:\\/\\/[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}(:[0-9]{1,5})?(\\/[^\\s]*)?$",
        message: i18n.t("validation.website"),
    },
    Area: {
        regex: "^[A-Za-z\\u0600-\\u06FF0-9 .'-]*$", // english , arabic , Numbers, Spaces, Dot, apostrophe, hyphen
        message: i18n.t("validation.area"),
    },
    StreetNo: {
        regex: "^[A-Za-z\\u0600-\\u06FF0-9\\-\\/#\\.]*$",
        message: i18n.t("validation.streetNo"),
    },
    Address: {
        regex: "^[A-Za-z\\u0600-\\u06FF0-9\\s,./()#'\\-]*$",  ///Only English and Arabic letters, numbers, and common address symbols (, . / ( ) # ' -) are allowed.
        message: i18n.t("validation.address"),
    },
    AlphaNumericAndSomeSpecialCharEngAr: {
        regex: "^[a-zA-Z0-9\\u0600-\\u06FF\\s,./()#'-]*$",
        message: i18n.t("validation.alphaNumericAndSomeSpecialCharEngAr"),
    },
    Note: {
        regex: "^[A-Za-z\\u0600-\\u06FF0-9\\s!@#$%^&()_\\-+=\\[\\]{};:'\",.<>?/\\\\|`~]+$",
        message: i18n.t("validation.note"),
    },
    Route: {
        regex: "^([a-zA-Z0-9_-]+\\/)*[a-zA-Z0-9_-]+$",
        message: i18n.t("validation.route"),
    },
    IconURL: {
        regex: "^([a-zA-Z0-9_\\-\\/]+)\\.(png|svg|ico|jpg|jpeg|gif)$",
        message: i18n.t("validation.iconURL"),
    },
    OnlyDigits: {
        regex: "^\\d+$",
        message: i18n.t("validation.onlyDigits"),
    },
    Description: {
        regex: "^[A-Za-z\\u0600-\\u06FF0-9\\s!@#$%^&*()_\\-+=\\[\\]{};:'\",.<>?/\\\\|`~]*$",
        message: i18n.t("validation.description"),
    },
    ArabicWithSpaceAndSymbols: {
        regex: "^[\\u0600-\\u06FF\\u0750-\\u077F\\u08A0-\\u08FF\\s\\-\\.',،؛؟()]+$",
        message: i18n.t("validation.arabicWithSpaceAndSymbols"),
    },
    ArabicWithSpaceAndHyphen: {
        regex: "^[\\u0600-\\u06FF\\s\\-]+$",
        message: i18n.t("validation.arabicWithSpaceAndHyphen"),
    },
    AddressArabic: {
        regex: "^[\\u0600-\\u06FF\\u0750-\\u077F\\u08A0-\\u08FF0-9\\s\\-\\.',،؛؟:\\/\\\\()#]+$",
        message: i18n.t("validation.addressArabic"),
    },
    AlphanumericWithSymbols: {
        regex: "^[A-Za-z0-9\\-&]+$",
        message: i18n.t("validation.alphanumericWithSymbols"),
    },
    AlphanumericWithCommonSymbols: {
        regex: "^[a-zA-Z0-9\\s,./()#'\\-]*$",
        message: i18n.t("validation.alphanumericWithCommonSymbols"),
    },
    ArabicLettersWithAddressSymbols: {
        regex: "^[\\u0600-\\u06FF\\u0750-\\u077F\\u08A0-\\u08FF0-9\\s\\-\\.',،؛؟:\\/\\\\()#]+$",
        message: i18n.t("validation.arabicLettersWithAddressSymbols"),
    },
    AlphabetsWithOutSpaceEngAr: {
        regex: "^[A-Za-z\\u0600-\\u06FF]+(?: [A-Za-z\\u0600-\\u06FF]+)*$",
        message: i18n.t("validation.alphabetsWithOutSpaceEngAr"),
    },
    OnlyDecimalNumber: {
        regex: "^[0-9]+(\\.[0-9]+)?$",
        message: i18n.t("validation.onlyDecimalNumber"),
    },
    DecimalWithNegative: {
        regex: "^-?[0-9]+(\\.[0-9]+)?$",
        message: i18n.t("validation.decimalWithNegative"),
    }

};
export default validationPatterns;  
// Usage in FormField